import { useEffect, useState, useRef } from "react";
import { Grid, Box, Typography, Paper, Divider } from "@mui/material";

import { auctionsLive } from "../../Model/Auction";
import { hostname } from "../../config";
import { getToken } from "../../Model/User";

const overlayStatuses = ["RESERVE_NOT_MET", "PASS", "UNSOLD", "SOLD"];

export default function AuctionLayout({ group }) {
  const token = getToken();
  const socketRef = useRef(null);

  const [auction, setAuction] = useState(null);
  const [lot, setLot] = useState(null);
  const [image, setImage] = useState(null);
  const [hp, setHp] = useState(0);
  const [status, setStatus] = useState("");
  const [rates, setRates] = useState({ USD: 0, EUR: 0, HKD: 0 });
  const [title, setTitle] = useState("");
  
  //-----------------------------------
  // INIT SOCKET
  //-----------------------------------
  useEffect(() => {
    if (!token) return;

    const socket = hostname(token);
    socketRef.current = socket;

    socket.on("connect", () => console.log("SOCKET CONNECTED:", socket.id));
    socket.on("connect_error", (err) => console.log("SOCKET ERROR:", err));

    return () => socket.disconnect();
  }, [token]);

  //-----------------------------------
  // FETCH AUCTION
  //-----------------------------------
  const LiveAuction = async () => {
    try {
      const res = await auctionsLive();
      if (!res?.success) return;

      const lotData = res.lot?.details?.[0] || {};
      const img = res.lot?.image_lot?.[0]?.img_url || null;

      const computedHp =
        !lotData.hp || lotData.hp === 0 ? lotData.starting_price : lotData.hp;
        setTitle(res.auction_details.auction_name);
      setAuction(res.auction_details);
      setLot(lotData);
      setImage(img);
      setHp(computedHp);

      // JOIN SOCKET ROOM
      if (socketRef.current) {
        socketRef.current.emit("joint_room", res.auction_details.auction_id);
        console.log("JOINED ROOM:", res.auction_details.auction_id);
      }
    } catch (err) {
      console.error("Error loading auction:", err);
    }
  };

  useEffect(() => {
    LiveAuction();
  }, []);

  //-----------------------------------
  // SOCKET EVENTS
  //-----------------------------------
  useEffect(() => {
    if (!socketRef.current) return;
    const socket = socketRef.current;

    const handleReceiveBidAmount = () => LiveAuction();
    const handleStatus = (data) => {
      setStatus(data.message);
      LiveAuction();
    };

    socket.on("receive_bid_amount", handleReceiveBidAmount);
    socket.on("receive_status", handleStatus);

    return () => {
      socket.off("receive_bid_amount", handleReceiveBidAmount);
      socket.off("receive_status", handleStatus);
    };
  }, []);

  //-----------------------------------
  // FETCH LIVE EXCHANGE RATES
  //-----------------------------------
  useEffect(() => {
    let interval;
    const fetchRates = async () => {
      try {
        const res = await fetch(
          "https://api.frankfurter.app/latest?from=PHP&to=USD,EUR,HKD"
        );
        const data = await res.json();
        console.log(" rate ",data)
        setRates({
          USD: data.rates.USD,
          EUR: data.rates.EUR,
          HKD: data.rates.HKD,
        });
      } catch (err) {
        console.error("Error fetching exchange rates:", err);
      }
    };

    fetchRates();
    interval = setInterval(fetchRates, 10000); // update every 10 sec

    return () => clearInterval(interval);
  }, []);

  //-----------------------------------
  // LOADING
  //-----------------------------------
  if (!auction || !lot)
    return <Typography sx={{ p: 4 }}>Loading auction…</Typography>;

  //-----------------------------------
  // UI
  //-----------------------------------
  return (
    <Grid container spacing={3} justifyContent="center" sx={{ p: 4, background: "#f5f6f8", minHeight: "100vh" }}>
      <Grid item xs={12} md={10}>
        <Paper elevation={4} sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 4, p: 4, borderRadius: 4, background: "#fff" }}>
          
          
          <Box sx={{ flex: 1 }}>
            <Box sx={{ mb: 3 }}>
              <img src="/static/media/dddd.f96bbcbee8c8c2f5e3ee.png" alt="Leon Logo" style={{ width: 180 }} />
            </Box>

            <Typography variant="h4" sx={{ fontWeight: 700 }}>{auction.auction_name}</Typography>
            <Divider sx={{ my: 2 }} />

            
            
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>Lot {lot.lot_number}</Typography>
          

<Typography 
  variant="h6" 
  sx={{ 
    mt: 1, 
    fontWeight: 500,
    whiteSpace: "pre-line", // ✅ Sinusunod ang \n (breakline) mula sa DB
    wordBreak: "break-word", // ✅ Iwas "overflow" kung sobrang haba ng isang salita
    lineHeight: 1.3
  }}
>
  {lot.artist}
</Typography>


<Typography 
  variant="h6" 
  sx={{ 
    color: "#777", 
    mb: 3,
    whiteSpace: "pre-line", // ✅ Sinusunod ang \n (breakline) mula sa DB
    wordBreak: "break-word",
    lineHeight: 1.3
  }}
>
  {lot.title}
</Typography>

            
            <Typography variant="h2" sx={{ fontWeight: "bold" }}>PHP {hp.toLocaleString()}</Typography>
            <Divider sx={{ my: 3 }} />

            
            
<Grid container spacing={1}>
  <Grid item xs={6}><Typography>USD ($)</Typography></Grid>
  <Grid item xs={6}>
    <Typography>{rates.USD ? (hp * rates.USD).toFixed(2) : "–"}</Typography>
  </Grid>

  <Grid item xs={6}><Typography>EUR (€)</Typography></Grid>
  <Grid item xs={6}>
    <Typography>{rates.EUR ? (Number(hp) * rates.EUR).toFixed(2) : "–"}</Typography>
  </Grid>

  <Grid item xs={6}><Typography>HKD (HK$)</Typography></Grid>
  <Grid item xs={6}>
    <Typography>{rates.HKD ? (hp * rates.HKD).toFixed(2) : "–"}</Typography>
  </Grid>
</Grid>

          </Box>

          
          <Box sx={{ flex: 0.9, display: "flex", justifyContent: "center", alignItems: "center", p: 2, position: "relative" }}>
            <Box component="img" src={image || "/fallback.jpg"} alt={lot.title} sx={{ width: "100%", maxWidth: 420, borderRadius: 3, objectFit: "contain" }} />

            {lot.lot_status && overlayStatuses.includes(lot.lot_status) && lot.lot_status !== "PENDING" && lot.lot_status !== "OPEN" && (
              <Box sx={{
                position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
                borderRadius: 3, backgroundColor: "rgba(0,0,0,0.6)",
                color: lot.lot_status === "SOLD" ? "#4caf50" : "#f44336",
                display: "flex", justifyContent: "center", alignItems: "center",
                fontWeight: "bold",
                fontSize: lot.lot_status === "RESERVE_NOT_MET" ? 65 : 100,
                textTransform: "uppercase",
                textAlign: "center", padding: 20, lineHeight: 1.2, wordWrap: "break-word",
                overflow: "hidden",
              }}>
                {lot.lot_status.replace(/_/g, " ")}
              </Box>
            )}
          </Box>

        </Paper>
      </Grid>
    </Grid>
  );
}
